import React from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
} from 'lucide-react';

interface PaginationControlsProps {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  // Dense, single-line toolbar variant: drops the vertical margins and lays the bar out in three
  // zones — the range + per-page selector on the LEFT, the page navigation CENTERED, and an
  // optional actions slot on the RIGHT.
  compact?: boolean;
  // compact only: show a small spinner before the range while a fetch is in flight.
  loading?: boolean;
  // compact only: right-aligned actions (e.g. the view/download/columns buttons).
  rightSlot?: React.ReactNode;
}

export const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  compact = false,
  loading = false,
  rightSlot,
}) => {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = totalItems === 0 ? 0 : Math.min(currentPage * pageSize, totalItems);

  if (compact) {
    return (
      <div className="grid w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 text-sm text-gray-600">
        {/* LEFT: range + per-page selector */}
        <div className="flex min-w-0 items-center gap-2">
          {loading && (
            <Loader2
              data-testid="results-loading"
              className="h-3.5 w-3.5 shrink-0 animate-spin text-gray-400"
            />
          )}
          <span className="min-w-0 flex-1 truncate">
            <span className="hidden sm:inline">
              แสดง {startItem.toLocaleString()}–{endItem.toLocaleString()} จาก{' '}
            </span>
            <span className="sm:hidden">ทั้งหมด </span>
            <span className="font-medium text-gray-900">
              {totalItems.toLocaleString()}
            </span>
            <span className="hidden sm:inline"> รายการ</span>
          </span>
        </div>

        {/* CENTER: page navigation */}
        <div className="flex min-w-max shrink-0 items-center justify-center gap-1 whitespace-nowrap">
          <button
            aria-label="ก่อนหน้า"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="whitespace-nowrap px-1 tabular-nums">
            หน้า {currentPage} / {totalPages}
          </span>
          <button
            aria-label="ถัดไป"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* RIGHT: per-page selector + actions. Hide page-size on narrow screens to keep the
            current-page control centered and prevent toolbar overlap. */}
        <div className="flex min-w-0 items-center justify-end gap-2">
          <select
            aria-label="จำนวนรายการต่อหน้า"
            value={pageSize}
            onChange={(e) => {
              onPageSizeChange(Number(e.target.value));
              onPageChange(1);
            }}
            className="hidden rounded border px-1.5 py-0.5 text-sm md:block"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}/หน้า
              </option>
            ))}
          </select>
          {rightSlot}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row items-center justify-between mt-4 mb-4 gap-3 md:gap-0">
      <div className="text-sm text-gray-700 text-center md:text-left">
        แสดง {startItem} ถึง {endItem} จาก {totalItems} รายการ
      </div>

      <div className="flex flex-wrap items-center gap-2 md:gap-4">
        <span className="text-sm text-gray-700 hidden sm:inline">จำนวนรายการต่อหน้า:</span>
        <select
          aria-label="จำนวนรายการต่อหน้า"
          value={pageSize}
          onChange={(e) => {
            onPageSizeChange(Number(e.target.value));
            onPageChange(1); // Reset to first page when changing page size
          }}
          className="border rounded px-2 py-1 text-sm w-20 sm:w-auto"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>

        <div className="flex items-center gap-1">
          {/* First/Last hidden on very small screens to keep UI compact */}
          <button
            aria-label="หน้าแรก"
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent hidden sm:inline-flex"
          >
            <ChevronsLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          <button
            aria-label="ก่อนหน้า"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          <span className="text-sm text-gray-700 px-2">
            หน้า {currentPage} / {totalPages}
          </span>

          <button
            aria-label="ถัดไป"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent"
          >
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>

          <button
            aria-label="หน้าสุดท้าย"
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="p-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent hidden sm:inline-flex"
          >
            <ChevronsRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaginationControls;
